import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { isoDate } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ScanDocumentBox from "@/components/ScanDocumentBox";
import Combobox from "@/components/Combobox";
import { MARQUES, getModelesForMarque } from "@/lib/marques";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Car } from "lucide-react";
import { toast } from "sonner";
import NewClientDialog from "@/components/NewClientDialog";

const empty = {
  client_id: "", client_nom: "", marque: "", modele: "", annee: "",
  plaque: "", vin: "", couleur: "", kilometrage: "", prix_vente: "",
  numero_vo: "",
  date_vente: isoDate(),
  date_depart: "",
  carte_grise_disponible: false,
  notes: "",
  acompte_montant: "",
  acompte_methode: "Espèces",
};

/**
 * Full "Nouvelle vente" form, reusable across pages (Dashboard, Ventes…).
 * Mirrors the form in /pages/Ventes.jsx so users have a consistent experience.
 */
export default function VenteFormDialog({
  triggerLabel = "Nouvelle vente",
  triggerVariant = "primary", // primary | outline
  triggerTestId = "vente-form-trigger",
  navigateAfterCreate = true,
  onCreated,
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadClients = async () => {
    try {
      const { data } = await api.get("/clients");
      setClients(data);
    } catch (err) {
      console.warn("Failed to load clients:", err);
    }
  };

  useEffect(() => { if (open) loadClients(); }, [open]);

  const startCreate = () => { setForm(empty); setOpen(true); };

  const handleClientChange = (id) => {
    const c = clients.find((x) => x.id === id);
    setForm((f) => ({ ...f, client_id: id, client_nom: c ? c.nom : f.client_nom }));
  };

  const handleClientCreated = (newClient) => {
    setClients((prev) => [newClient, ...prev.filter((c) => c.id !== newClient.id)]);
    setForm((f) => ({ ...f, client_id: newClient.id, client_nom: newClient.nom }));
    toast.success(`Client ${newClient.nom} lié à la vente`);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        annee: form.annee ? Number(form.annee) : null,
        kilometrage: form.kilometrage ? Number(form.kilometrage) : 0,
        prix_vente: Number(form.prix_vente || 0),
        client_id: form.client_id || null,
        acompte_montant: form.acompte_montant ? Number(form.acompte_montant) : 0,
      };
      const { data } = await api.post("/ventes", payload);
      toast.success("Vente enregistrée");
      setOpen(false);
      setForm(empty);
      onCreated?.(data);
      if (navigateAfterCreate) navigate(`/ventes/${data.id}`);
    } catch (err) {
      const msg = err.response?.data?.detail;
      const text = Array.isArray(msg)
        ? msg.map((m) => m.msg || JSON.stringify(m)).join(" · ")
        : typeof msg === "string" ? msg : "Erreur lors de la création";
      toast.error(text);
    } finally {
      setSaving(false);
    }
  };

  const triggerClass =
    triggerVariant === "outline"
      ? "rounded-sm"
      : "bg-klein hover:bg-blue-800 text-white rounded-sm";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant={triggerVariant === "outline" ? "outline" : "default"}
          onClick={startCreate}
          data-testid={triggerTestId}
          className={triggerClass}
        >
          <Car size={14} className="mr-1" /> {triggerLabel}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Nouvelle vente</DialogTitle>
        </DialogHeader>

        <ScanDocumentBox
          onExtract={(data) => {
            setForm((f) => ({
              ...f,
              marque: data.marque || f.marque,
              modele: data.modele || f.modele,
              annee: data.annee || f.annee,
              plaque: (data.plaque || f.plaque || "").replace(/-/g, "").toUpperCase(),
              vin: data.vin || f.vin,
              couleur: data.couleur || f.couleur,
              kilometrage: data.kilometrage ?? f.kilometrage,
            }));
          }}
        />
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <Label className="text-xs uppercase tracking-wider">Client existant</Label>
              <Select value={form.client_id || undefined} onValueChange={handleClientChange}>
                <SelectTrigger data-testid="vente-form-client" className="rounded-sm mt-1"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs uppercase tracking-wider">Nom client *</Label>
                <Input data-testid="vente-form-clientnom" value={form.client_nom} onChange={(e) => setForm({ ...form, client_nom: e.target.value, client_id: "" })} required className="rounded-sm mt-1" />
              </div>
              <NewClientDialog onCreated={handleClientCreated} triggerLabel="+ Nouveau" testId="vente-form-newclient" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Marque</Label>
              <Combobox
                testId="vente-form-marque"
                value={form.marque}
                onChange={(v) => setForm({ ...form, marque: v, modele: "" })}
                options={MARQUES}
                placeholder="Tapez ou choisissez…"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Modèle</Label>
              <Combobox
                testId="vente-form-modele"
                value={form.modele}
                onChange={(v) => setForm({ ...form, modele: v })}
                options={getModelesForMarque(form.marque)}
                placeholder={form.marque ? "Tapez ou choisissez…" : "Sélectionnez d'abord la marque"}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Année</Label>
              <Input data-testid="vente-form-annee" type="number" inputMode="decimal" value={form.annee} onChange={(e) => setForm({ ...form, annee: e.target.value })} className="rounded-sm mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Plaque *</Label>
              <Input data-testid="vente-form-plaque" value={form.plaque} onChange={(e) => setForm({ ...form, plaque: e.target.value.toUpperCase() })} required className="rounded-sm mt-1 font-mono uppercase" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">VIN</Label>
              <Input data-testid="vente-form-vin" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value })} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Couleur</Label>
              <Input data-testid="vente-form-couleur" value={form.couleur} onChange={(e) => setForm({ ...form, couleur: e.target.value })} className="rounded-sm mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Kilométrage</Label>
              <Input data-testid="vente-form-km" type="number" inputMode="decimal" value={form.kilometrage} onChange={(e) => setForm({ ...form, kilometrage: e.target.value })} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Prix de vente *</Label>
              <Input data-testid="vente-form-prix" type="number" inputMode="decimal" step="0.01" value={form.prix_vente} onChange={(e) => setForm({ ...form, prix_vente: e.target.value })} required className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">N° VO</Label>
              <Input data-testid="vente-form-numero-vo" value={form.numero_vo} onChange={(e) => setForm({ ...form, numero_vo: e.target.value })} className="rounded-sm mt-1" placeholder="Stock interne" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 items-end">
            <div>
              <Label className="text-xs uppercase tracking-wider">Date vente</Label>
              <Input data-testid="vente-form-date" type="date" value={form.date_vente} onChange={(e) => setForm({ ...form, date_vente: e.target.value })} className="rounded-sm mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Date de départ</Label>
              <Input data-testid="vente-form-date-depart" type="date" value={form.date_depart} onChange={(e) => setForm({ ...form, date_depart: e.target.value })} className="rounded-sm mt-1" />
            </div>
            <label className="flex items-center gap-2 px-3 py-2 border border-border rounded-sm cursor-pointer mt-6 select-none">
              <input
                type="checkbox"
                data-testid="vente-form-cartegrise"
                checked={!!form.carte_grise_disponible}
                onChange={(e) => setForm({ ...form, carte_grise_disponible: e.target.checked })}
                className="w-4 h-4 accent-klein"
              />
              <span className="text-sm">Carte grise disponible</span>
            </label>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Notes</Label>
            <Textarea data-testid="vente-form-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm mt-1" rows={2} />
          </div>

          {/* Acompte initial (facultatif) */}
          <div className="p-3 border border-emerald-200 bg-emerald-50/40 rounded-sm">
            <div className="text-[11px] tracking-[0.18em] uppercase text-emerald-800 font-bold mb-2">
              Acompte initial (facultatif)
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Montant acompte</Label>
                <Input
                  data-testid="vente-form-acompte"
                  type="number" inputMode="decimal" step="0.01"
                  value={form.acompte_montant}
                  onChange={(e) => setForm({ ...form, acompte_montant: e.target.value })}
                  placeholder="Ex: 500"
                  className="rounded-sm mt-1 bg-white"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Méthode</Label>
                <Select value={form.acompte_methode} onValueChange={(v) => setForm({ ...form, acompte_methode: v })}>
                  <SelectTrigger data-testid="vente-form-acompte-methode" className="rounded-sm mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Espèces">Espèces</SelectItem>
                    <SelectItem value="Virement">Virement</SelectItem>
                    <SelectItem value="Chèque">Chèque</SelectItem>
                    <SelectItem value="Carte">Carte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Si renseigné, un paiement sera automatiquement lié à cette vente lors de la création.
            </p>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="vente-form-submit" className="bg-klein hover:bg-blue-800 text-white rounded-sm">
              {saving ? "Enregistrement…" : "Enregistrer la vente"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
